// Prequalification System v1 — public funnel API (anonymous; no session).
// Mirrors PrequalificationDtos.cs. Endpoints are IP-rate-limited server-side
// (finalize: PortalRateLimits.PrequalPolicy; guide: the generous
// PrequalLivePolicy) and live under /api/portal/prequalification so the
// portal client's isolation holds. The live sizing meter computes CLIENT-side
// (pages/prequalify/prequalSizing.ts) — nothing calls /compute anymore (the
// backend endpoint remains for tooling).

import { portalApi } from "./client";
import type { PrequalAttribution } from "../pages/prequalify/prequalAttribution";

export type PrequalRole = "Buyer" | "Seller";
export type PrequalOutcome = "Pass" | "SoftDecline";
/** Fit-check answers (prequal-v2 polarity: Yes is favorable). The seller
 *  franchise question also stores "not-a-franchise" (= pass). */
export type KnockoutAnswer = "yes" | "no" | "unsure" | "not-a-franchise";

/** Which package a buyer asked for at the top of the funnel. Mirrors the C#
 *  `PrequalBuyerDocumentPath`, which is string-stored — these ARE the wire
 *  values, not a client-side label set.
 *
 *  🔴 `InternalBuyer` is the odd member: the other two choose DOCUMENTS and
 *  size nothing, while this one routes the engine onto a different sizing basis
 *  entirely (the business's cash flow rather than the buyer's capital). Read it
 *  as a path, not a package.
 *
 *  Pinned member-for-member against the C# enum — and against the staff copy of
 *  this union — by `prequalEnumUnionsMatchTheServer.test.ts`. */
export type PrequalBuyerDocumentPath = "LetterOnly" | "LetterAndDealSheet" | "InternalBuyer";

/** How an internal buyer is connected to the business they are buying. Mirrors
 *  the C# `PrequalInternalRelationship`, string-stored — these ARE the wire
 *  values. Three qualify and no others: a supplier, a customer or an adviser is
 *  not an internal buyer however well they know the business.
 *
 *  Pinned to the C# enum by `prequalEnumUnionsMatchTheServer.test.ts`. */
export type PrequalInternalRelationship =
  | "FamilyMember"
  | "EmployeeOrManager"
  | "PartnerOrCoOwner";

/** Mirrors `BuyerType` (Core/Enums/Buyers/BuyerCompanyEnums.cs). Declared here,
 *  not imported from the staff `api/buyers`, because portal and staff code stay
 *  isolated; both mirrors are pinned to the C# by
 *  test/prequalEnumUnionsMatchTheServer.test.ts. */
export type PrequalBuyerType = "Individual" | "CompanyOwnedOrLed" | "InvestmentGroupOrFund";
export type PrequalEquitySource = "CompanyFundsOnHand" | "CommittedByInvestors" | "StillBeingRaised";
export type PrequalOperatorArrangement =
  | "CompanyPrincipal"
  | "HiredGeneralManager"
  | "ExistingManagement"
  | "DesignatedPerson";

/** Client mirror of `BuyerAcquirer.IsCompanyBuyer`'s type half. */
export function isCompanyBuyerType(type: PrequalBuyerType | null | undefined): boolean {
  return type === "CompanyOwnedOrLed" || type === "InvestmentGroupOrFund";
}

export interface PrequalBuyerAnswers {
  /** The trade or CATEGORY the buyer is shopping in. Never a specific business
   *  — that is `targetBusinessName`. */
  targetIndustry?: string | null;
  /** The ONE business the buyer already has in mind, in their own words — a
   *  name, a listing title, a link, or nothing. Optional, never validated.
   *
   *  It is projected onto a searchable column server-side, and it PRINTS ON NO
   *  DOCUMENT AND IN NO EMAIL — an unvalidated free-text field a buyer typed is
   *  not something to put in front of a counterparty's broker, and a letter
   *  naming the wrong business is worse than one naming none. */
  targetBusinessName?: string | null;
  /** "exploring" | "250k-500k" | "500k-1m" | "1m-2.5m" | "2.5m-5m" | "5m-10m". */
  priceBand?: string | null;

  // ── The named deal ──────────────────────────────────────────────────────
  // 🔴 WHAT THESE DO DEPENDS ENTIRELY ON `documentPath`, AND THE TWO ANSWERS
  // ARE OPPOSITE. This block used to say, flatly, "these choose and price a
  // DOCUMENT. They size nothing: qualification is still cash + experience +
  // reserves, and the sizing mirror reads none of them." That remains true on
  // `LetterOnly` and `LetterAndDealSheet`, and it is FALSE on `InternalBuyer`,
  // where `targetAnnualCashFlow` sizes the entire qualification, the asking
  // price is what the Deal Sheet is priced on, and `cashAvailable` sizes
  // nothing at all. Read every note below with the path in hand.

  /** Which package this buyer asked for. Absent on a submission taken before
   *  the question existed — "not asked", which is neither answer. */
  documentPath?: PrequalBuyerDocumentPath | null;
  /** Price of the business they are looking at.
   *
   *  🔴 A ZERO IS AN ABSENT PRICE and is sent as null — nobody buys a business
   *  for nothing, so a typed 0 is the same statement as a blank field. Wrapped
   *  in `nullIfNotPositive` at the call site, exactly like the seller funnel's
   *  asking price. */
  targetPurchasePrice?: number | null;
  /** The business's annual free cash flow as the buyer reports it. A zero is
   *  absent for the same reason as the price — the sheet sizes coverage from
   *  it. Also wrapped.
   *
   *  🔴 ON `InternalBuyer` THIS AND THE PRICE ABOVE ARE REQUIRED AND REFUSED
   *  RATHER THAN NORMALISED. There they are not a document's pricing, they ARE
   *  the qualification: the range is sized from this cash flow. A blank or a
   *  zero is rejected at the field and again at the finalize boundary
   *  (`ValidateInternalBuyerFigures`), because a range sized from a blank is
   *  not a conservative answer, it is a meaningless one. */
  targetAnnualCashFlow?: number | null;
  /** What they intend to put down on THIS deal.
   *
   *  🔴 OPTIONAL AND LEGITIMATELY ZERO — the opposite rule to the two above,
   *  and the part a tidy-up breaks. Phoenix requires no down payment and sets
   *  no minimum, so a stated 0 is an answer and is sent VERBATIM (never
   *  `nullIfNotPositive`). null = not stated.
   *
   *  🔴 IT REACHES NO DOCUMENT. This comment used to end "and the sheet falls
   *  back to the program-standard 5% of price" — the exact sentence the C#
   *  remarks on `PrequalBuyerAnswers.TargetDownPayment` retract and quote as
   *  wrong. There is no fallback: the Deal Sheet derives its down payment as
   *  `price x DownPaymentMidpoint` on every sheet, whatever this holds, and the
   *  qualified price already CONTAINS that down payment — so wiring this figure
   *  in would subtract the same money twice. On those two paths its only reader
   *  is the staff answer panel, and the null-versus-zero distinction is about
   *  what the RECORD says rather than about any arithmetic.
   *
   *  🔴 ON `InternalBuyer` IT SIZES, AND A BLANK IS ZERO. There the price is
   *  `note + down` exactly as the seller path derives one, so this figure moves
   *  the qualified range — and a blank means $0 rather than the program-standard
   *  5%, because the premise of an internal purchase is that no down payment is
   *  required and deriving one would invent the very thing the path exists to
   *  say is unnecessary. An explicit 0 is the same answer stated out loud. It
   *  stays OPTIONAL on all three paths. */
  targetDownPayment?: number | null;
  /** When they expect to close — `yyyy-MM-dd`. Display only, feeds no
   *  arithmetic on either tier. */
  targetPurchaseDate?: string | null;

  // ── The internal purchase ───────────────────────────────────────────────
  // Sent on `InternalBuyer` and on no other path. None of the three feeds the
  // engine: the relationship is what the letter names and what staff verify as
  // a condition of closing, the tenure is context, and the co-buyer count is a
  // staff-side fact that prints on no document.

  /** Which of the three relationships the buyer asserts. REQUIRED on the
   *  internal path — it is what the letter names, and it is the thing staff
   *  later verify. */
  internalRelationship?: PrequalInternalRelationship | null;
  /** How long they have worked there, in whole years. Asked of an employee or
   *  manager and of nobody else.
   *
   *  🔴 THERE IS NO MINIMUM AND IT GATES NOTHING. A manager of eight months is
   *  as eligible as one of eight years; the answer is context for the
   *  specialist. A reader looking for the threshold it enforces will not find
   *  one. */
  internalYearsWithBusiness?: number | null;
  /** How many people are buying in alongside them — a COUNT and no names.
   *
   *  🔴 STAFF-SIDE ONLY: it prints on no document. Naming co-buyers would put
   *  unverified people on paperwork a counterparty reads; the count tells a
   *  specialist what shape the deal is without asserting anything about
   *  anyone. */
  internalCoBuyerCount?: number | null;
  /** CASH ONLY, and it stays that way. Non-cash assets the buyer intends to
   *  put toward the same down payment live in `cashEquivalents`; the figure the
   *  sizing mirror divides by the down-payment percentages is
   *  `totalDownPaymentCapacity(answers)`, never this field alone. */
  cashAvailable: number;
  /** Non-cash assets the buyer intends to put toward the down payment —
   *  self-reported, never verified at prequalification.
   *
   *  ABSENT/null means the buyer named none, which is the path every
   *  application written before this existed is on: absent contributes exactly
   *  0 to the capacity, so a cash-only buyer sizes byte-identically to before.
   *  A zeroed object would NOT — it would put five selected-but-empty
   *  categories on every legacy application. Mirrors C#
   *  `PrequalBuyerAnswers.CashEquivalents`. */
  cashEquivalents?: PrequalCashEquivalents | null;
  /** Working capital the buyer expects to have after closing, in dollars.
   *  Optional; a typed ZERO is a real answer, so it is sent verbatim rather
   *  than blanked the way a zero price or a zero cash flow is.
   *
   *  🔴 It feeds NOTHING in the engine. The reserves factor ladder keys on
   *  `reservesBand` and only on that (`prequalSizing.ts`, and its C# source of
   *  truth). This amount is captured, stored and shown; changing what the
   *  ladder reads would change what every capital-path buyer qualifies for,
   *  which is a credit decision and not a funnel change. */
  reservesAmount?: number | null;
  /** The banded answer — the ONLY reserves input the engine reads. */
  reservesBand?: string | null;
  /** "never-owned" | "owned-smaller" | "owned-comparable" | "owned-multiple"
   *  (prequal-v3 rungs; legacy values degrade to rung 1). */
  ownershipExperience: string;
  /** "none" | "small-team" | "department-pnl" | "executive-pnl"
   *  (prequal-v3, optional; missing → rung 1). */
  managementExperience?: string;
  /** "new" | "adjacent" | "direct" | "direct-5plus". */
  industryFit: string;
  /** "operator" | "oversight-manager" | "hands-off" — the active-management
   *  gate (a non-operator answer is the absentee path) AND the involvement
   *  sizing factor. */
  involvement: string;
  /** AAMA path — the fit-check follow-up shown only for a non-operator
   *  involvement: already operates a similar business and will install a
   *  manager. A non-operator with this flag is a conditional PASS; without it,
   *  a soft decline. */
  aamaPath: boolean;
  // ── Company & sponsor buyers (owner brief 2026-10-08) ──────────────────
  // 🔴 These ride HERE, inside `buyer`, never top-level on the finalize body:
  // the top-level `website` field is the bot honeypot, and the draft and
  // assisted paths rebuild the buyer from these answers alone. The server's
  // draft reader is strict — this interface and `PrequalBuyerAnswers` (C#)
  // ship together.
  /** "Who is buying?" Null/absent = not asked = individual. */
  buyerType?: PrequalBuyerType | null;
  /** Required for a company buyer type. */
  acquiringCompanyName?: string | null;
  /** The person's title in the acquiring company — required for a company
   *  buyer type. */
  acquiringCompanyTitle?: string | null;
  acquiringCompanyWebsite?: string | null;
  acquiringCompanyAbout?: string | null;
  /** How the company's equity contribution is funded. Company buyers only. */
  equitySource?: PrequalEquitySource | null;
  /** Who will manage the business full time when the buyer is not the
   *  operator: a company buyer's designated operator or an individual
   *  overseer's. Satisfies the active-management gate like `aamaPath`. */
  operatorArrangement?: PrequalOperatorArrangement | null;
  operatorName?: string | null;
  /** Where the buyer is open to operating the business — a descriptor, never a
   *  sizing gate. */
  operatingGeography?: PrequalOperatingGeography | null;
  /** Fit-check key → "yes" | "no" | "unsure" (Yes favorable). */
  knockouts: Record<string, string>;
  goals?: string | null;
}

/**
 * Non-cash assets a buyer intends to put toward their down payment — "other
 * assets toward your down payment" in every borrower-facing surface (the words
 * "cash equivalents" are the wire name and never appear on screen). Mirrors C#
 * `PrequalCashEquivalents`.
 *
 * FIVE CATEGORIES, AND THEY ARE CLOSED — named nullable amounts rather than a
 * list of {type, amount} pairs, so uniqueness, ordering and unknown-token
 * rejection come for free on both sides of the wire.
 *
 * An ABSENT amount means "category not selected" and is distinct from a
 * selected category worth $0: only a category with a value contributes to
 * sizing or renders on a document, so an untouched one leaves no trace. Send
 * absent, never 0.
 *
 * `retirementAccounts` is deliberately NOT folded into `securities`. Both are
 * account-held paper, but a 401(k) carries withdrawal cost a taxable brokerage
 * position does not, so the two stay independently haircuttable
 * (PREQUAL_CASH_EQUIVALENT_HAIRCUTS in pages/prequalify/prequalSizing.ts).
 *
 * ONE shared `note` covers the whole section, not one per category.
 */
export interface PrequalCashEquivalents {
  /** Equity in real estate the buyer owns. */
  realEstateEquity?: number | null;
  /** Publicly traded stock and other marketable securities. */
  securities?: number | null;
  /** Equity / ownership stake in a business the buyer already owns. */
  businessEquity?: number | null;
  /** Retirement and investment accounts — 401(k), IRA and similar. */
  retirementAccounts?: number | null;
  /** Anything the four named categories do not cover; explained in `note`. */
  otherAssets?: number | null;
  /** The ONE shared free-text note covering every selected category. */
  note?: string | null;
}

/** Operating-presence descriptor (buyer). A letter descriptor + later matching
 *  signal — never a sizing gate. Mirrors C# PrequalOperatingGeography. */
export interface PrequalOperatingGeography {
  /** "near" | "region" | "anywhere". */
  mode?: string | null;
  /** Home city ("City, ST"), captured on the target step. */
  homeCity?: string | null;
  /** True when open to relocating (mode "anywhere"). */
  willingToRelocate: boolean;
}

// 🔴 RETIRED in prequal-v4: type PrequalDownPaymentMode = "percent" | "amount".
// One down-payment input remains (an optional dollar amount that may be zero),
// so there is no mode to record. Mirrors the C# enum's removal.

export interface PrequalSellerAnswers {
  businessName?: string | null;
  industry?: string | null;
  location?: string | null;
  yearsInOperation?: number | null;
  annualRevenue: number;
  annualCashFlow: number;
  askingPrice?: number | null;
  ownerHoursBand?: string | null;
  absenceOutcome?: string | null;
  revenuePersonallyDrivenBand?: string | null;
  customerConcentration?: string | null;
  booksQuality?: string | null;
  revenueSpike?: string | null;
  /** The down payment the seller expects from their buyer, in DOLLARS.
   *  OPTIONAL and legitimately ZERO — Phoenix requires no downpayment, sets no
   *  minimum, and no downpayment funds flow through Phoenix. The ONLY
   *  down-payment input since prequal-v4; `price = note + down` always.
   *  (`downPaymentBand` and `downPaymentMode` are retired — historical payloads
   *  may still carry them and they are simply not read.) */
  downPaymentAmount?: number | null;
  transitionWillingness?: string | null;
  reasonForSelling?: string | null;
  knockouts: Record<string, string>;
}

export interface PrequalReadinessFlag {
  key: string;
  label: string;
  level: "ok" | "watch";
}

export interface PrequalEngineResult {
  outcome: PrequalOutcome;
  knockoutReasons: string[];
  unsureFlags: string[];
  // Buyer figures (null for sellers)
  buyingPowerBase?: number | null;
  buyingPowerLow?: number | null;
  buyingPowerHigh?: number | null;
  experienceTier?: string | null;
  experienceCeiling?: number | null;
  qualifiedAmount?: number | null;
  noteAmount?: number | null;
  conditionalAbsentee?: boolean;
  /** The five applied prequal-v3 sizing factors (buyer only). */
  factors?: { key: string; rung: string; factor: number }[] | null;
  /** THE RULE APPLIES — ownership rung 1 AND management rung 1, so the cap was
   *  consulted. It does NOT mean anything was capped: under roughly $50,000 of
   *  capacity the clamp is a no-op. Drives arithmetic that re-applies the same
   *  ceiling to a what-if figure. */
  firstTimerCapApplied?: boolean;
  /** THE CAP BOUND — the headline or the band top was actually pulled down to
   *  the first-timer cap. Every caption that tells a reader something was
   *  capped reads this one. */
  firstTimerCapBound?: boolean;
  // Seller figures (null for buyers)
  /** Supportable note at RangeLowNoteDscr (1.80x) — the BOTTOM of the dollar
   *  range. A HIGHER DSCR forces a SMALLER loan, so 1.80 makes the small
   *  number and 1.65 the big one. Do not reorder these on magnitude. */
  noteLow?: number | null;
  /** Supportable note at RangeHighNoteDscr (1.65x) — the TOP of the range. */
  noteHigh?: number | null;
  /** Midpoint of noteLow/noteHigh. Depends only on cash flow, never on the
   *  down payment — it is the anchor both down-payment modes convert through. */
  noteMid?: number | null;
  /** Retained alias, repointed: mirrors `noteHigh`. */
  maxNote?: number | null;
  /** Retained alias, repointed: mirrors `noteLow`. */
  conservativeNote?: number | null;
  supportablePriceLow?: number | null;
  supportablePriceHigh?: number | null;
  coverageAtAsking?: number | null;
  /** Down payment as a FRACTION of price — DERIVED for display only, never an
   *  input. Zero on a zero-down deal. */
  downPaymentPercent?: number | null;
  /** The down payment in DOLLARS, as the seller stated it. May be zero. */
  downPaymentAmount?: number | null;
  /** Retained alias of `downPaymentPercent` — seller only. */
  sellerDownPaymentPct?: number | null;
  /** Note ÷ annual free cash flow — the FINANCED multiple, capped at 5.0x by
   *  construction. NOT a price multiple: the cap is on the financed portion. */
  financedMultiple?: number | null;
  /** Actual coverage at the note the range's TOP prints, AFTER the 5x clamp —
   *  computed, not the DSCR that sized it (~1.7271x, not 1.65x). */
  coverageAtNote?: number | null;
  /** Days 0–90 monthly, P&I at the transition rate. */
  transitionMonthly?: number | null;
  /** Day 90 onward monthly — pool P&I plus illustrative vendor fee income. */
  poolMonthly?: number | null;
  /** Down + 3 transition payments + 9 pool distributions. CASH ONLY — carries
   *  nothing derived from a note balance. */
  firstYearCash?: number | null;
  readinessFlags: PrequalReadinessFlag[];
  engineVersion: string;
  // ── prequal-v7: the two payment schedules (1 November 2026 terms) ──────
  // Mirror of the trailing PrequalEngineResult members. Null / absent on a row
  // sized before prequal-v7, on a decline, and on a result with no free cash
  // flow to size (the capital letter path). 20 = the 240-month schedule, 10 =
  // the 120-month schedule. Money to the cent; ratios to four places.
  /** The free cash flow the two schedules were sized on. */
  scheduleCashFlow?: number | null;
  /** Maximum Financed Amount (§2 T5) on the 20-year schedule — an AMOUNT
   *  FINANCED, never a price. */
  maxFinanced20?: number | null;
  /** Maximum Financed Amount on the 10-year schedule. */
  maxFinanced10?: number | null;
  /** Which T5 limit set it: "FiveTimesCashFlow" |
   *  "CoverageOnBuyerNotePrincipal" | "ProgramMaximum". */
  binding20?: string | null;
  binding10?: string | null;
  /** The purchase figure: the Maximum plus the stated down payment. */
  priceAt20?: number | null;
  priceAt10?: number | null;
  /** Buyer Note Principal at the Maximum (amount financed + 4.95% setup fee). */
  buyerNotePrincipal20?: number | null;
  buyerNotePrincipal10?: number | null;
  /** Free cash flow ÷ 12 months' P&I on the Buyer Note Principal (T5(ii)). */
  sizingCoverage20?: number | null;
  sizingCoverage10?: number | null;
  /** Origination Coverage Ratio (T7). STAFF VIEWS ONLY (D2). */
  originationCoverage20?: number | null;
  originationCoverage10?: number | null;
  /** The Maximum is under the $250,000 minimum amount financed. */
  belowMinimum20?: boolean | null;
  belowMinimum10?: boolean | null;
  /** The named deal's price less the stated down payment (null when no price
   *  was named). */
  statedAmountFinanced?: number | null;
  /** The named deal's amount financed is at or under each schedule's Maximum. */
  statedWithinMaximum20?: boolean | null;
  statedWithinMaximum10?: boolean | null;
  /** The Origination Coverage Ratio at the named deal's amount financed
   *  (staff views only). */
  statedOriginationCoverage20?: number | null;
  statedOriginationCoverage10?: number | null;
}

export interface PrequalComputeRequest {
  role: PrequalRole;
  buyer?: PrequalBuyerAnswers;
  seller?: PrequalSellerAnswers;
}

export interface PrequalGuideRequest extends PrequalComputeRequest {
  stepKey?: string;
}

export interface PrequalFinalizeRequest extends PrequalComputeRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  consentAccepted: boolean;
  /** Honeypot — hidden on the real form, always sent empty. */
  website?: string;
  /** White-label: the partner slug this submission was captured under (from
   *  the /prequalify/{slug} route). Omitted for the native Phoenix funnel. */
  partnerSlug?: string;
  /** Tracked invite token from `?invite={token}`, when this person arrived
   *  through an invite rather than organically.
   *
   *  A LIVE invite is authoritative over `partnerSlug` server-side — the
   *  sender chose which site the invite went out from, so a tampered slug
   *  cannot redirect their credit. An unknown, expired, or revoked token is
   *  IGNORED and the submission lands as ordinary self-serve traffic; it is
   *  never a rejection, because losing a finished questionnaire to protect a
   *  reporting nicety is the wrong trade. */
  inviteToken?: string;
  /** The /buy landing page's continuation token (`blt` on the URL). Attaches
   *  the completion to the lead that page already created — but only when the
   *  email typed here also matches it, so this alone authorises nothing. */
  buyLandingToken?: string;
  /** The per-advertisement link token (`ad` on the URL, decision D2), carried
   *  from a buyer team's `/buy/{slug}?ad=` page into the funnel. Honoured
   *  server-side only on a team-site completion whose advertisement belongs to
   *  that team; an unknown, archived or wrong-team token is ignored and the
   *  lead still lands. Never a rejection. */
  advertisementToken?: string;
  /** Where the visit came from — the UTM set, the landing page and the form id,
   *  captured at the FIRST page view (the only one carrying the ad's query
   *  string) and held in sessionStorage for the rest of the run. Omitted for
   *  direct traffic. The server sanitises and length-caps it before storing. */
  attribution?: PrequalAttribution;
}

/** What the funnel learns from an invite token. Deliberately thin — it crosses
 *  to an unauthenticated caller.
 *
 *  For a DEAD link (`isLive: false`) the recipient's own name and email are
 *  withheld: a stale link in the wrong hands must not read back contact
 *  details. `invitedByLabel` still comes through so the friendly page can name
 *  who to ask for a fresh one. An unknown token is shaped identically to an
 *  expired one, so a prober learns nothing from the difference. */
export interface PrequalInviteResolution {
  found: boolean;
  /** `Sent | Opened | Started | Completed | ClosedBySibling | Revoked |
   *  Expired | Unavailable`.
   *
   *  `Unavailable` is deliberately vague — it is what the server reports when
   *  the sending broker has been off-ramped, and this value reaches the
   *  anonymous public funnel, so it must not tell a stranger that a named
   *  broker is in trouble. The dead-link card branches on it and says only that
   *  the link stopped working. */
  status: string;
  isLive: boolean;
  role?: PrequalRole | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  partnerSlug?: string | null;
  invitedByLabel?: string | null;
}

/**
 * The kinds of GENERATED, per-submission artifact.
 *
 * 🔴 The Program Brochure is deliberately NOT a member, and adding one would be a
 * type that lies. Every consumer of this union — {@link PrequalDocumentLink} here,
 * `PrequalDocument`/`PrequalDocumentLink` in `prequalSite.ts`, and the staff
 * `PrequalificationDocType` — carries a `documentId`, which is a
 * `PrequalificationDocument.Id`. A brochure has no such row: it is a
 * `ProgramBrochure`, one per role Phoenix-wide, and it travels on its own
 * {@link ProgramBrochureLink} shape. The C# enum has three members for the same
 * reason, so a fourth member here could never appear on the wire.
 */
export type PrequalDocType = "Letter" | "DealSheet" | "Analysis";

export interface PrequalDocumentLink {
  documentId: string;
  docType: PrequalDocType;
  url: string;
  expiresAtUtc: string;
}

/**
 * A time-limited read link to the role's Program Brochure — the uploaded PDF that
 * explains the program in full.
 *
 * `available: false` with a null `url` is the "no brochure on file" answer, and it
 * is DATA rather than an error: nothing is seeded and no PDF ships in the
 * repository, so it is what a fresh environment returns for both roles. The result
 * screen hides the offer on it instead of rendering a broken link.
 */
export interface ProgramBrochureLink {
  role: PrequalRole;
  available: boolean;
  url?: string | null;
  expiresAtUtc?: string | null;
  fileName?: string | null;
}

export interface PrequalFinalizeResult {
  submissionId: string;
  result: PrequalEngineResult;
  documents: PrequalDocumentLink[];
  /** Human reference (PQ-B-YYYY-NNNN / PQ-S-YYYY-NNNN) minted at finalize. */
  referenceNumber?: string | null;
  /** Validity horizon (finalize + 90 days) — rendered on the result screen. */
  validThroughUtc?: string | null;
  /** True when the finalize email (documents attached) was actually sent —
   *  mirrors the trailing `bool EmailSent` on the C# finalize result. */
  emailSent?: boolean;
  /**
   * The package this buyer asked for — mirrors the trailing
   * `PrequalBuyerDocumentPath? BuyerDocumentPath` on the C# finalize result.
   *
   * 🔴 The result screen's card list is a static array for a buyer, so without
   * this a letter-only applicant is shown a greyed-out "Deal Sheet" card with a
   * dead Preview button — which reads as "your Deal Sheet failed" on the one
   * surface the buyer sees.
   *
   * Read the CHOICE, never the absence of a link: presence cannot tell "never
   * asked for" from "failed to generate", and a buyer who DID ask wants to keep
   * seeing the card, because the dead one is their only sign anything is wrong.
   *
   * Null on a seller, and on every submission finalized before the fork shipped
   * — both of which are "wanted both", the compatibility rule the fork rests on.
   */
  buyerDocumentPath?: PrequalBuyerDocumentPath | null;
  /**
   * The price the buyer's own Deal Sheet is BUILT ON — the figure that document
   * prints. Mirrors the trailing `decimal? DealSheetPrice` on the C# finalize
   * result.
   *
   * 🔴 **NOT A CAPACITY FIGURE, and that distinction is the whole reason it is on
   * the wire.** Every buyer figure on {@link PrequalEngineResult} is derived from
   * the buyer's WALLET — `qualifiedAmount` is a capacity headline — while the
   * document is priced `namedPrice ?? qualifiedAmount`, and the price the buyer
   * NAMED lives on the answers, which this result does not carry. So the Deal
   * Sheet card had nothing to read but `qualifiedAmount` and printed it beside a
   * link to a document showing a different number. Measured on a live staging
   * run: the card said $3,610,000, the document said $9,000,000.
   *
   * 🔴 **SERVER-COMPUTED, through the one shared rule (`PrequalDealSheetPricing`)
   * that also prices the document — so the card and the document cannot answer
   * differently.** Do NOT re-derive it here from the stored answers: that would
   * make this screen a second implementation of the same money question, free to
   * drift.
   *
   * ⚠️ Earlier drafts added "and it would be wrong for the reader who most needs
   * it — a buyer opening their result link on another device has no
   * sessionStorage". That half is FALSE: `PrequalResultPage` reads `finalized`
   * only from `loadFunnelState` and redirects when it is absent, so a
   * cross-device reader never reaches this card. Kept as a correction because a
   * reader could otherwise build a cross-device result read believing this field
   * is what makes it work. The drift argument stands alone.
   *
   * NULL for a seller, for a letter-only buyer (there is no Deal Sheet to price),
   * and for any result persisted before this field existed. 🔴 **Null means NAME
   * NO FIGURE — never fall back to `qualifiedAmount`.** That fallback is the
   * defect itself, and it fires on precisely the rows where the two differ.
   */
  dealSheetPrice?: number | null;
  /**
   * The partner site this submission was ATTRIBUTED to — mirrors the trailing
   * `string? PartnerSlug` on the C# finalize result. Null on a Phoenix-native
   * application.
   *
   * Not the slug that was REQUESTED. A live invite beats the URL server-side,
   * so this is the brand the submission is actually recorded against, and the
   * result screen skins itself from it rather than from the address bar — which
   * on an assisted application says nothing at all, and which even on the public
   * funnel is not the authority.
   *
   * OPTIONAL, and the difference between `null` and absent is load-bearing: a
   * blob persisted before this field existed reads back `undefined`, which the
   * result screen treats as "defer to the route" — its previous behaviour —
   * rather than as "this application has no site".
   */
  partnerSlug?: string | null;
  /**
   * The credential the result screen spends to ask for FRESH download links.
   *
   * 🔴 **This response is the only time it is ever emitted.** There is no email
   * fallback and no second issuance, so a client that drops it is stuck replaying
   * `documents` — whose SAS URLs expire fifteen minutes after finalize.
   *
   * OPTIONAL, and absent means "no re-fetch is possible": a soft decline (nothing
   * was generated), or a blob persisted before this field existed. Both fall back to
   * rendering the stored `documents`, which is exactly what this screen did before.
   */
  resultToken?: string | null;
}

/** Fresh links for one submission's own generated documents. Carries links and
 *  nothing else — the result screen already holds the name, email, answers and
 *  figures in its own session storage, so re-serving them here would widen the
 *  token from "re-issue these links" to "read this application back". */
export interface PrequalResultDocuments {
  documents: PrequalDocumentLink[];
}

/** Deal Explorer (§6) — scenario deal-sheet PDF request. Numbers are plain
 *  dollars; the date is ISO yyyy-MM-dd. */
export interface PrequalDealSheetPreviewRequest {
  purchasePrice: number;
  downPayment: number;
  annualCashFlow: number;
  expectedPurchaseDate?: string;
  /**
   * The prequalification this scenario is modelled against, when there is one.
   *
   * The server derives the partner BRAND from this record and takes nothing
   * else from it. Sending the submission rather than the brand is the point: a
   * slug is public (it is in the funnel URL), so accepting one would let anyone
   * mint a partner-branded Phoenix Capital deal sheet carrying any numbers they
   * like, on a document whose whole purpose is to be handed to a seller.
   *
   * OPTIONAL. The Explorer is reachable with no application at all, and that
   * case renders Phoenix-native — which is correct there, not a degradation.
   */
  submissionId?: string;
  /**
   * Whose scenario this is. Client-supplied, which is what keeps the server's
   * read of the submission narrowed to the brand alone.
   *
   * The asymmetry is deliberate and it is the same line drawn twice: the
   * figures and the name are the buyer's own what-if on a sheet stamped
   * ILLUSTRATIVE, and a forged one is a document someone made for themselves.
   * Forged partner letterhead is a different thing entirely.
   */
  buyerFullName?: string;
}

export const prequalificationApi = {
  finalize: (req: PrequalFinalizeRequest) =>
    portalApi.post<PrequalFinalizeResult>("/prequalification/finalize", req),
  /** Null prose = AI unavailable — the panel hides silently. A failure
   *  (incl. 429) is never retried; the caller just shows no tip. */
  guide: (req: PrequalGuideRequest) =>
    portalApi.post<string | null>("/prequalification/guide", req),
  /** Deal Explorer — server-rendered scenario deal sheet (binary PDF), with
   *  the file name the server chose. The name follows the brand (a
   *  partner-branded sheet must not arrive called "phoenix-…"), so it is
   *  resolved where the brand is known and carried back rather than
   *  re-derived here. */
  dealSheetPreview: (req: PrequalDealSheetPreviewRequest) =>
    portalApi.postNamedBlob("/prequalification/deal-sheet/preview", req),
  /** Resolve a tracked invite token. ALWAYS 200 — an unknown or dead token
   *  comes back `isLive: false` rather than a 404, so the funnel renders one
   *  friendly page for every failure mode. Marks the first open server-side,
   *  which is why it is a read the funnel makes exactly once on landing. */
  resolveInvite: (token: string) =>
    portalApi.get<PrequalInviteResolution>(
      `/prequalification/invite/${encodeURIComponent(token)}`),
  /**
   * FRESH download links for the applicant's own documents.
   *
   * The token is the whole lookup — there is no submission id in this call. Every
   * resolution failure (malformed, unknown, expired, soft-declined,
   * generated-nothing) is the same bodyless 404, so the caller's only sensible
   * branch is "did it come back", and its fallback in every case is to keep
   * rendering the links it already holds.
   */
  resultDocuments: (resultToken: string) =>
    portalApi.get<PrequalResultDocuments>(
      `/prequalification/result/${encodeURIComponent(resultToken)}`),
  /**
   * The role's Program Brochure link.
   *
   * Anonymous and UNTOKENED, by owner ruling: one file, identical for every
   * applicant, carrying no personal data and nothing derived from anyone's answers
   * — there is nothing to scope it to. Deliberately NOT folded into
   * {@link resultDocuments}: behind that token it would become unreachable on
   * exactly the pages whose token has lapsed.
   *
   * Always 200. `available: false` is the "none uploaded" answer.
   */
  programBrochureLink: (role: PrequalRole) =>
    portalApi.get<ProgramBrochureLink>(
      `/prequalification/program-brochure/${role === "Buyer" ? "buyer" : "seller"}/link`),
};
